import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { FadeUp, SectionHead } from "@/components/Reveal";
import StatsBand from "@/components/StatsBand";
import { useLang } from "@/lib/i18n";

const ABOUT_BG = "/api/files/dfe-website/uploads/about-stage.jpg";

const STATS = [
  { n: "16", en: "YEARS", zh: "年历程 · 自 2009", en2: "years since 2009" },
  { n: "40+", en: "SHOWS PRODUCED", zh: "场演出与音乐节", en2: "shows & festivals" },
  { n: "30+", en: "GLOBAL ARTISTS", zh: "位合作国际巨星", en2: "global superstars" },
];

export default function About() {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} data-testid="about" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <SectionHead no="01" en="THE FACTORY" zh="关于夢工廠" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <FadeUp className="md:col-span-7">
            {lang === "zh" ? (
              <>
                <p className="font-zh text-xl md:text-3xl leading-relaxed text-[#f5f5f5]">
                  娛樂夢工廠（Dream Factory Entertainment）于{" "}
                  <span className="text-[#ffde00]">2009 年在马来西亚创立</span>
                  ，由音乐人 Isaac（小马）与艺人经理 Eileen 联合创办。
                </p>
                <p className="font-zh text-base md:text-lg text-[#a1a1aa] leading-relaxed mt-8">
                  两位创始人在台湾、香港、中国大陆、东南亚及欧美市场深耕多年，怀着同一个目标创立 DFE
                  —— 为马来西亚及全球观众带来更多艺人与演出。从 BLACK EYED PEAS、DAESUNG、TAEYANG 到 YE，
                  DFE 一直站在国际级舞台的幕后，负责艺人管理、制作与主办执行。
                </p>
              </>
            ) : (
              <>
                <p className="font-zh text-xl md:text-3xl leading-relaxed text-[#f5f5f5]">
                  Dream Factory Entertainment was{" "}
                  <span className="text-[#ffde00]">founded in Malaysia in 2009</span> by songwriter Isaac (Xiao Ma) and artist manager Eileen.
                </p>
                <p className="font-zh text-base md:text-lg text-[#a1a1aa] leading-relaxed mt-8">
                  With years of experience across Taiwan, Hong Kong, China, Southeast Asia and Western markets, the founders
                  built DFE with one goal — bring more artists and performances to audiences in Malaysia and worldwide. From
                  BLACK EYED PEAS, DAESUNG and TAEYANG to YE, DFE stands behind international stages as artist manager,
                  producer and organiser.
                </p>
              </>
            )}
            <div className="mt-10 border-l-2 border-[#ffde00] pl-5">
              <p className="font-display text-2xl md:text-3xl uppercase text-[#f5f5f5]">Where Music Meets Dreams</p>
              <p className="font-mono2 text-xs text-[#a1a1aa] tracking-[0.25em] mt-2">— DREAM FACTORY ENTERTAINMENT</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-5">
            <div className="relative overflow-hidden border border-white/10">
              <motion.img
                style={{ y: imgY }}
                src={ABOUT_BG}
                alt="Stage lights"
                className="w-full h-[420px] md:h-[520px] object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <p className="absolute bottom-5 left-5 font-mono2 text-[10px] text-[#ffde00] tracking-[0.3em]">
                LIVE · ON STAGE · SINCE 2009
              </p>
            </div>
          </FadeUp>
        </div>

        <StatsBand />
      </div>
    </section>
  );
}
